import type { ReactNode } from 'react';
import './recordDetailView.css';

export interface RecordDetailBadge {
  text: string;
  tone?: 'success' | 'warning' | 'danger' | 'neutral' | 'info';
}

export function RecordDetailView({ children }: { children: ReactNode }) {
  return <div className="record-detail-view">{children}</div>;
}

export function RecordDetailHero({
  title,
  subtitle,
  badges = []
}: {
  title: string;
  subtitle?: string | null;
  badges?: RecordDetailBadge[];
}) {
  return (
    <header className="record-detail-hero">
      <div className="record-detail-hero__text">
        <div className="record-detail-hero__title">{title}</div>
        {subtitle ? <div className="record-detail-hero__subtitle">{subtitle}</div> : null}
      </div>
      {badges.length > 0 ? (
        <div className="record-detail-hero__badges">
          {badges.map((b) => (
            <span
              key={b.text}
              className={`record-detail-badge record-detail-badge--${b.tone ?? 'neutral'}`}
            >
              {b.text}
            </span>
          ))}
        </div>
      ) : null}
    </header>
  );
}

export function RecordDetailPanel({
  title,
  children
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="record-detail-panel">
      <div className="record-detail-panel__head">{title}</div>
      <div className="record-detail-panel__body">{children}</div>
    </section>
  );
}

export function RecordDetailGrid({
  children,
  columns = 2
}: {
  children: ReactNode;
  columns?: 1 | 2;
}) {
  return (
    <div
      className={
        columns === 1
          ? 'record-detail-grid record-detail-grid--1col'
          : 'record-detail-grid'
      }
    >
      {children}
    </div>
  );
}

export function RecordDetailGridFull({ children }: { children: ReactNode }) {
  return <div className="record-detail-grid__full">{children}</div>;
}

function displayValue(value: ReactNode, empty = '—') {
  if (value == null) return empty;
  if (typeof value === 'string' && value.trim() === '') return empty;
  return value;
}

export function RecordDetailItem({
  label,
  value,
  children,
  empty,
  multiline = false
}: {
  label: string;
  value?: ReactNode;
  children?: ReactNode;
  empty?: string;
  multiline?: boolean;
}) {
  if (children != null) {
    return (
      <div className="record-detail-item">
        <div className="record-detail-item__label">{label}</div>
        {children}
      </div>
    );
  }

  const shown = displayValue(value, empty);
  const isEmpty = shown === (empty ?? '—');

  return (
    <div className="record-detail-item">
      <div className="record-detail-item__label">{label}</div>
      {multiline ? (
        <div
          className={`record-detail-text-block${isEmpty ? ' record-detail-text-block--empty' : ''}`}
        >
          {shown}
        </div>
      ) : (
        <div className={`record-detail-item__value${isEmpty ? ' record-detail-item__value--empty' : ''}`}>
          {shown}
        </div>
      )}
    </div>
  );
}
